/**
 * Parse pasted JSON against the library envelope — structural gate ONLY,
 * and a FORGIVING one: markdown fences and prose around the JSON are
 * stripped (models wrap their output), missing groupWeights default to
 * equal shares, and absent rule layers mean null. Never throws; failures
 * come back NAMED so the paste step can say what is actually wrong.
 * Semantic checks live in ../validate.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  FORMULA_CLIPBOARD_KIND,
  FORMULA_CLIPBOARD_SUPPORTED_VERSIONS,
} from '@/features/flow-optimizer/clipboard/formula-clipboard.types';
import { isGroupPayload } from '@/features/flow-optimizer/clipboard/formula-clipboard.serialize';
import type { FormulaConfigPayload } from '@/features/flow-optimizer/clipboard/formula-clipboard.types';

import type {
  PackagePayload,
  ParsedLibraryPayload,
  RuleSetPayload,
} from './library-payload.types';

export type LibraryParseFailure =
  | 'not-json'
  | 'wrong-kind'
  | 'unsupported-version'
  | 'unknown-type'
  | 'bad-data';

export type LibraryParseResult =
  | { ok: true; payload: ParsedLibraryPayload }
  | { ok: false; failure: LibraryParseFailure; type?: string };

/** Cut pasted text down to the outermost JSON object: strip a ```json fence
 *  and any prose the model wrapped around its answer. */
function extractJsonCandidate(text: string): string {
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(text);
  const body = (fenced ? fenced[1] : text).trim();
  const start = body.indexOf('{');
  const end = body.lastIndexOf('}');
  return start >= 0 && end > start ? body.slice(start, end + 1) : body;
}

export function parseLibraryJsonDetailed(text: string): LibraryParseResult {
  if (!text || typeof text !== 'string') return { ok: false, failure: 'not-json' };
  let raw: unknown;
  try {
    raw = JSON.parse(extractJsonCandidate(text));
  } catch {
    return { ok: false, failure: 'not-json' };
  }
  if (!raw || typeof raw !== 'object') return { ok: false, failure: 'not-json' };
  const p = raw as Record<string, unknown>;
  if (p.kind !== FORMULA_CLIPBOARD_KIND) return { ok: false, failure: 'wrong-kind' };
  if (
    typeof p.version !== 'number' ||
    !FORMULA_CLIPBOARD_SUPPORTED_VERSIONS.includes(p.version)
  ) {
    return { ok: false, failure: 'unsupported-version' };
  }
  return matchType(p);
}

/** Back-compat boolean-style variant (null on any failure). */
export function parseLibraryJson(text: string): ParsedLibraryPayload | null {
  const result = parseLibraryJsonDetailed(text);
  return result.ok ? result.payload : null;
}

function matchType(p: Record<string, unknown>): LibraryParseResult {
  const label = typeof p.label === 'string' ? p.label : undefined;
  const type = typeof p.type === 'string' ? p.type : 'missing';
  const data = p.data;
  switch (type) {
    case 'group':
      return isGroupPayload(data)
        ? { ok: true, payload: { type: 'group', label, data } }
        : { ok: false, failure: 'bad-data', type };
    case 'formula': {
      const formula = normalizeFormulaHalf(data);
      return formula
        ? { ok: true, payload: { type: 'formula', label, data: formula } }
        : { ok: false, failure: 'bad-data', type };
    }
    case 'rule-set': {
      const rules = normalizeRuleHalf(data);
      return rules
        ? { ok: true, payload: { type: 'rule-set', label, data: rules } }
        : { ok: false, failure: 'bad-data', type };
    }
    case 'package': {
      const pkg = normalizePackage(data);
      return pkg
        ? { ok: true, payload: { type: 'package', label, data: pkg } }
        : { ok: false, failure: 'bad-data', type };
    }
    default:
      return { ok: false, failure: 'unknown-type', type };
  }
}

/** groups required; missing groupWeights become equal shares, and each group
 *  gets mode/dateRange defaults — models routinely omit boilerplate fields
 *  and that must not sink the paste (we normalize downstream anyway). */
function normalizeFormulaHalf(value: unknown): FormulaConfigPayload | null {
  if (!value || typeof value !== 'object') return null;
  const f = value as Record<string, unknown>;
  if (!Array.isArray(f.groups)) return null;
  if (f.groupWeights !== undefined && !Array.isArray(f.groupWeights)) return null;
  const groups = f.groups.map((group) => {
    const g = (group ?? {}) as Record<string, unknown>;
    return {
      ...g,
      mode: g.mode === 'sliders' ? 'sliders' : 'blocks',
      dateRange: typeof g.dateRange === 'string' ? g.dateRange : 'last-7-days',
    };
  });
  return {
    ...f,
    groups,
    groupWeights: Array.isArray(f.groupWeights)
      ? f.groupWeights
      : groups.map(() => 100),
  } as unknown as FormulaConfigPayload;
}

const LAYER_KEYS = ['paths', 'landers', 'offers'] as const;

/** Canonicalize the rules half. Accepts BOTH shapes models produce:
 *  {excludeRules: {…}, capacityRules} and the FLATTENED
 *  {paths, landers, offers, capacityRules} (a real ChatGPT output). */
function normalizeRuleHalf(value: unknown): RuleSetPayload | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const rs = value as Record<string, unknown>;
  if (rs.capacityRules !== undefined && !Array.isArray(rs.capacityRules)) {
    return null;
  }
  const tree = rs.excludeRules;
  if (tree !== undefined) {
    if (!tree || typeof tree !== 'object' || Array.isArray(tree)) return null;
    return rs as unknown as RuleSetPayload;
  }
  if (!LAYER_KEYS.some((k) => k in rs) && !('capacityRules' in rs)) return null;
  return {
    excludeRules: {
      paths: (rs.paths ?? null) as RuleSetPayload['excludeRules']['paths'],
      landers: (rs.landers ?? null) as RuleSetPayload['excludeRules']['landers'],
      offers: (rs.offers ?? null) as RuleSetPayload['excludeRules']['offers'],
    },
    ...(Array.isArray(rs.capacityRules)
      ? { capacityRules: rs.capacityRules as RuleSetPayload['capacityRules'] }
      : {}),
  };
}

export function isRuleSetPayload(value: unknown): value is RuleSetPayload {
  return normalizeRuleHalf(value) !== null;
}

export function isPackagePayload(value: unknown): value is PackagePayload {
  return normalizePackage(value) !== null;
}

function normalizePackage(value: unknown): PackagePayload | null {
  if (!value || typeof value !== 'object') return null;
  const pkg = value as Record<string, unknown>;
  const formula = normalizeFormulaHalf(pkg.formula);
  const rules = normalizeRuleHalf(pkg.rules);
  if (!formula || !rules) return null;
  return { ...pkg, formula, rules } as unknown as PackagePayload;
}
